export type MemberType =
  | 'admin'
  | 'silk'
  | 'junior'
  | 'pupil'
  | 'door_tenant'
  | 'senior_clerk'
  | 'junior_clerk'
  | 'staff'
  | 'solicitor';

export interface SolicitorProfileSummary {
  id: string;
  firmContactId: string;
  firmName: string | null;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  memberType: MemberType;
  avatarUrl: string | null;
  jobTitle?: string | null;
  chambersId: string;
  barristerId: string | null;
  /**
   * TRUE when the password this session was opened with was chosen by somebody else — the
   * temporary password an admin hands over at invite, or one an admin set for them. Such a
   * password is known to at least two people, so it is a one-time entry ticket: the app blocks
   * everything behind ForcePasswordChangeModal until the user sets their own. Optional because a
   * session persisted before this shipped will not carry it; absent is treated as `false`.
   */
  mustChangePassword?: boolean;
  solicitorProfile?: SolicitorProfileSummary | null;
}

export interface AuthChambers {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  currency: string;
  jurisdiction: string;
  /** IANA timezone the chambers operates in (admin-set; default Europe/London). Members see it read-only. */
  timezone: string;
  /**
   * 'solo'     — one barrister operating as their own practice; they OWN the workspace.
   * 'chambers' — a multi-member chambers; organisation settings belong to the administrator.
   *
   * Server-resolved from Chambers.workspaceType. NEVER infer this on the client (a head-count of
   * barristers is not the same fact — see docs/SETTINGS-MODEL.md §1). Optional only so a session
   * persisted before this field existed still parses; absent is treated as 'chambers' (fail closed).
   */
  workspaceType?: 'solo' | 'chambers';
}

export interface LoginInput {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/** A completed sign-in: the session is established and the app may proceed. */
export interface LoginSession {
  accessToken: string;
  user: AuthUser;
  chambers: AuthChambers;
  permissions: string[];
}

/**
 * The password was correct and NOTHING ELSE HAPPENED YET.
 *
 * The server returns this instead of a session when the account has TOTP enabled
 * (auth.service.ts → `{ mfa_required: true, challengeToken }`). The challenge is a 5-minute,
 * single-purpose token — it is not a credential for anything except POST /mfa/verify.
 */
export interface MfaChallenge {
  mfa_required: true;
  challengeToken: string;
}

/**
 * Login has TWO possible outcomes and the type says so.
 *
 * It used to name only the session, so `mfa_required` — which the API has always been able to
 * return — was invisible to the compiler. The caller destructured `data.user`, got `undefined`,
 * and an account with MFA on could not sign in at all. Modelling this as a union is what makes
 * dropping the challenge branch a TYPE ERROR rather than a silent dead end.
 */
export type LoginResponse = LoginSession | MfaChallenge;

export function isMfaChallenge(r: LoginResponse): r is MfaChallenge {
  return (r as MfaChallenge).mfa_required === true;
}

export interface MeResponse {
  user: AuthUser;
  chambers: AuthChambers;
  permissions: string[];
}
